import React, { useState, useEffect, useMemo, useCallback } from "react";
import { CiLock, CiUnlock } from "react-icons/ci";
import { RxEyeOpen } from "react-icons/rx";
import { VscEyeClosed } from "react-icons/vsc";
import "react-phone-input-2/lib/style.css";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import apiKey from "../../../config/apiKey";
import axios from "axios";
import { useTranslation } from "react-i18next";

/* ==================== UI HELPERS ==================== */
const GradientButton = React.memo(({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B] text-white font-semibold text-sm px-5 py-2 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
  >
    {children}
  </button>
));

const Input = React.memo(({ className = "", ...p }) => (
  <input
    {...p}
    className={`outline-none px-3 py-2 w-full bg-[#F9FBFF] border border-[#F0F2F7] rounded-lg focus:ring-2 focus:ring-[#EE5F2B]/30 ${className}`}
  />
));

const Card = React.memo(({ title, children, className = "" }) => (
  <div className={`rounded-2xl border border-[#E9EDF3] bg-white shadow-sm p-5 ${className}`}>
    {title && <h3 className="text-lg font-semibold text-gray-800 mb-3">{title}</h3>}
    {children}
  </div>
));

/* ============= STRICTLY-ISOLATED PASSWORD FORM ============= */
const PasswordForm = React.memo(function PasswordForm({ token }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  // input handlers live here; parent rerenders won't touch this component
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }));
  }, []);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setErrors((prev) => {
      if (!prev?.[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const normalizeNewPasswordErrors = useMemo(() => {
    const np = errors?.new_password;
    if (!np) return [];
    if (Array.isArray(np)) return np;
    if (typeof np === "string") return [np];
    return [];
  }, [errors]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!token) return;
      setIsLoading(true);
      try {
        await axios.post(`${apiKey}/api/edolutions/teacher/newpassword`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        toast.success(t("teacherDash.securityPrivacy.passwordUpdateSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });

        setFormData({ password: "", new_password: "", new_password_confirmation: "" });
        setErrors({});
      } catch (err) {
        const apiErrors = err.response?.data?.errors;
        if (apiErrors) setErrors(apiErrors);
        else setErrors({ general: t("teacherDash.otherDetails.unexpectedError") });
      } finally {
        setIsLoading(false);
      }
    },
    [formData, token]
  );

  return (
    <form onSubmit={handleSubmit} className="mt-3" key="password-form">
      <label className="text-xs font-semibold text-gray-600">{t("teacherDash.securityPrivacy.currentPasswordLabel")}</label>
      <Input
        placeholder={t("teacherDash.securityPrivacy.currentPasswordPlaceholder")}
        type="password"
        autoComplete="current-password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        onBlur={handleBlur}
        className="mt-2"
      />
      {errors.password && <p className="text-red-500 text-sm mt-1">{t("teacherDash.securityPrivacy.invalidCredentialsError")}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="relative">
          <label className="text-xs font-semibold text-gray-600">{t("teacherDash.securityPrivacy.newPasswordLabel")}</label>
          <Input
            type={showPassword ? "text" : "password"}
            placeholder={t("teacherDash.securityPrivacy.newPasswordPlaceholder")}
            autoComplete="new-password"
            name="new_password"
            value={formData.new_password}
            onChange={handleChange}
            onBlur={handleBlur}
            className="mt-2 pr-10"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-2 bottom-2.5 text-gray-500"
            aria-label={t("teacherDash.securityPrivacy.togglePasswordVisibility")}
          >
            {showPassword ? <RxEyeOpen /> : <VscEyeClosed />}
          </button>
        </div>

        <div className="relative">
          <label className="text-xs font-semibold text-gray-600">{t("teacherDash.securityPrivacy.confirmPasswordLabel")}</label>
          <Input
            type={showPassword ? "text" : "password"}
            placeholder={t("teacherDash.securityPrivacy.confirmPasswordPlaceholder")}
            autoComplete="new-password"
            name="new_password_confirmation"
            value={formData.new_password_confirmation}
            onChange={handleChange}
            onBlur={handleBlur}
            className="mt-2 pr-10"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-2 bottom-2.5 text-gray-500"
            aria-label={t("teacherDash.securityPrivacy.togglePasswordVisibility")}
          >
            {showPassword ? <RxEyeOpen /> : <VscEyeClosed />}
          </button>
        </div>
      </div>

      {normalizeNewPasswordErrors.length > 0 && (
        <ul className="text-red-500 text-sm mt-2 list-disc list-inside">
          {normalizeNewPasswordErrors.map((msg, i) => (
            <li key={i}>{msg}</li>
          ))}
        </ul>
      )}
      {errors.general && <p className="text-red-500 text-sm mt-2">{errors.general}</p>}

      <div className="mt-4">
        <GradientButton type="submit" disabled={isLoading || !token}>
          {isLoading ? t("teacherDash.common.saving") : t("teacherDash.securityPrivacy.savePasswordButton")}
        </GradientButton>
      </div>
    </form>
  );
});

/* ================================== PAGE ================================== */
const Privacy = () => {
  const { t } = useTranslation();
  // Stable teacher/token
  const teacher = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("edo-teacher"));
    } catch {
      return null;
    }
  }, []);
  const token = teacher?.token ?? "";

  // Profile state (kept separate from the PasswordForm)
  const [id, setId] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const [twoFA, setTwoFA] = useState(false);
  const [keepSomePrivate, setKeepSomePrivate] = useState(false);
  const [emailPrivate, setEmailPrivate] = useState(false);

  // Fetch profile once per token
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let alive = true;

    (async () => {
      try {
        const res = await axios.get(`${apiKey}/api/edolutions/teacher/teacher-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        const p = res.data?.[0];
        if (p) {
          setId(p.id);
          setStatus(p.status);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [token]);

  // Optimistic status toggle (no refetch -> avoids churn)
  const updateStatus = useCallback(async () => {
    if (!id || !token) return;
    try {
      await axios.put(`${apiKey}/api/edolutions/teacher/statuspublicprivate/${id}`, [], {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus((s) => (s === "Public" ? "Private" : "Public"));
      toast.success("Visibility updated", {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
        style: { background: "#2A3A54", color: "#fff" },
      });
    } catch (error) {
      console.error("Error updating visibility status!", error);
    }
  }, [id, token]);

  return (
    <div className="flex items-center xl:p-5 sm:p-0 justify-center">
      <div className="w-full border rounded-3xl shadow-xl p-5 px-6 md:px-10 bg-white">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">{t("teacherDash.common.settings")}</h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-6 border-b pb-3 text-sm font-semibold text-gray-700 mt-5">
          <NavLink
            to=".././profile-setting-tech"
            className={({ isActive }) =>
              isActive ? "text-[#EE5F2B] border-b-2 border-[#EE5F2B]" : "hover:text-[#2A3A54]"
            }
          >
            {t("teacherDash.settingTabs.profileSettings")}
          </NavLink>
          <NavLink
            to=".././specialization-tech"
            className={({ isActive }) =>
              isActive ? "text-[#EE5F2B] border-b-2 border-[#EE5F2B]" : "hover:text-[#2A3A54]"
            }
          >
            {t("teacherDash.securityPrivacy.specializationWpTab")}
          </NavLink>
          <NavLink
            to=".././other-detail"
            className={({ isActive }) =>
              isActive ? "text-[#EE5F2B] border-b-2 border-[#EE5F2B]" : "hover:text-[#2A3A54]"
            }
          >
            {t("teacherDash.settingTabs.otherDetails")}
          </NavLink>
          <span className="text-[#EE5F2B] border-b-2 border-[#EE5F2B]">{t("teacherDash.settingTabs.privacySecurity")}</span>
        </div>

        {/* 4 Boxes layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Password & Two-Factor */}
          <Card title={t("teacherDash.securityPrivacy.passwordTwoFactorCardTitle")}>
            <p className="text-sm font-medium text-gray-700">{t("teacherDash.securityPrivacy.passwordSectionLabel")}</p>
            {/* Isolated form: parent rerenders won't affect typing */}
            <PasswordForm token={token} />
            {/* 2FA Toggle */}
            <div className="mt-7 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("teacherDash.securityPrivacy.twoFactorAuthLabel")}</p>
                <p className="text-xs text-gray-500">{t("teacherDash.securityPrivacy.twoFactorAuthDesc")}</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={twoFA}
                  onChange={(e) => setTwoFA(e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-gray-300 rounded-full peer focus:outline-none peer-checked:bg-[#2A3A54] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>
          </Card>

          {/* Profile Picture Visibility */}
          <Card title={t("teacherDash.securityPrivacy.profilePictureVisibilityCardTitle")}>
            <div className="flex items-center justify-between">
              <div className="text-md">
                <p className="font-medium">{t("teacherDash.securityPrivacy.currentLabel")} {status || (loading ? t("teacherDash.common.loading") : "—")}</p>
                <p className="text-sm text-gray-500 mt-1">{t("teacherDash.securityPrivacy.switchPublicPrivateDesc")}</p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={updateStatus}
                  disabled={!id || !token}
                  className={`text-sm flex items-center gap-1 border px-3 py-1 rounded-lg ${
                    status === "Public"
                      ? "text-green-600 border-green-500"
                      : "text-gray-600 border-gray-400 hover:border-gray-600"
                  }`}
                >
                  <CiUnlock /> {t("teacherDash.securityPrivacy.publicLabel")}
                </button>
                <button
                  type="button"
                  onClick={updateStatus}
                  disabled={!id || !token}
                  className={`text-xs flex items-center gap-1 border px-3 py-1 rounded-lg ${
                    status === "Private"
                      ? "text-green-600 border-green-500"
                      : "text-gray-600 border-gray-400 hover:border-gray-600"
                  }`}
                >
                  <CiLock /> {t("teacherDash.securityPrivacy.privateLabel")}
                </button>
              </div>
            </div>

            <div className="mt-12 text-sm ">
              <div className="font-medium mb-8 border border-green-500 shadow-lg rounded-lg">
                <p className="p-2 m-2">
                  <span className="underline mb-2">{t("teacherDash.securityPrivacy.publicLabel")}</span> <br />
                  {t("teacherDash.securityPrivacy.publicDesc")}
                </p>
              </div>
              <div className="font-medium mb-8 border border-green-500 shadow-lg rounded-lg">
                <p className="p-2 m-2">
                  <span className="underline">{t("teacherDash.securityPrivacy.privateLabel")}</span> <br />
                  {t("teacherDash.securityPrivacy.privateDesc")}
                </p>
              </div>
            </div>
          </Card>

          {/* Work History Privacy */}
          <Card title={t("teacherDash.securityPrivacy.workHistoryPrivacyCardTitle")}>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-700">
                {t("teacherDash.securityPrivacy.workHistoryPrivacyDesc")}
              </p>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={keepSomePrivate}
                  onChange={(e) => setKeepSomePrivate(e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-gray-300 rounded-full peer focus:outline-none peer-checked:bg-[#2A3A54] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>
          </Card>

          {/* Email Privacy */}
          <Card title={t("teacherDash.securityPrivacy.emailPrivacyCardTitle")}>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-700">{t("teacherDash.securityPrivacy.emailPrivacyDesc")}</p>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={emailPrivate}
                  onChange={(e) => setEmailPrivate(e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-gray-300 rounded-full peer focus:outline-none peer-checked:bg-[#2A3A54] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
